//DEV NOTE: Use similar architecture for restructuring IndexList and DetailsTable elements
import { cloneDeep, isObject } from 'lodash';
import CacheManager from './cacheManager.js';

export default {
  data() {
    return {
      formCacheManager: new CacheManager(),
    };
  },
  computed: {
    computedForm() {
      const cacheKey = this.formCacheManager.createKey(this.form);

      if (this.formCacheManager.has(cacheKey))
        return this.formCacheManager.get(cacheKey);

      // If not in cache, compute the result
      const array2dForm = Array.isArray(this.form[0]) ? this.form : [this.form];

      const result = array2dForm.map(array1dForm =>
        this.restructureFormElementProperties(array1dForm)
      );

      // Store the result in cache
      this.formCacheManager.set(cacheKey, result);

      return result;
    },
  },
  methods: {
    restructureFormElementProperties(array1dForm) {
      // Create a cache key for array step (all elements of form page) 
      const cacheKey = this.formCacheManager.createKey(array1dForm);

      // Check if the result is already in cache, if yes, return result without any further processing
      if (this.formCacheManager.has(cacheKey))
        return this.formCacheManager.get(cacheKey);

      const result = array1dForm.map(element => this.restructureFormElement(element));

      // Store the result in cache
      this.formCacheManager.set(cacheKey, result);

      return result;
    },
    restructureFormElement(element) {
      if (!element) return element;

      // Create a cache key for each element
      const cacheKey = this.formCacheManager.createKey(element);

      // Check if the result is already in cache, if yes, return result without any further processing
      if (this.formCacheManager.has(cacheKey))
        return this.formCacheManager.get(cacheKey);

      element = this.setDefaultShowProperty(element);
      element = this.restructureColsProperties(element);
      element = this.restructureLabelProperties(element);
      element = this.restructureOnlyLabel(element);

      // Process nested fields in formElement.section
      if (element.section?.fields) {
        element.section.fields = element.section.fields.map(nestedElement =>
          this.restructureFormElement(nestedElement)
        );
      }

      // Store the result in cache
      this.formCacheManager.set(cacheKey, element);
      return element;
    },
    setDefaultShowProperty(element) {
      //set default value here
      const DEFAULT_SHOW = true;

      const hasShow = Object.hasOwn(element, 'show');
      element['show'] = Boolean(hasShow ? element.show : DEFAULT_SHOW);

      return element;
    },
    restructureColsProperties(element) {
      let tempCols = {
        cols: undefined,
        sm: undefined,
        md: undefined,
        lg: undefined,
        xl: undefined,
      };

      //optimisation feature. skip structure fixing when structure has already been built
      if (isObject(element.cols)
        && element.cols.cols
        && Object.prototype.hasOwnProperty.call(element.cols, 'sm')) {
        return element;
      }

      //value can either be like { cols: { cols: 5, sm: 2, ... }} or { cols: 5, sm: 3, ... }
      const isColsObject = isObject(element.cols);
      const cols = isColsObject ? element.cols : element;
      for (var key in tempCols) {
        const value = cols[key];
        tempCols[key] = value || this.defaultColsSize(element, key);
      }

      //seperate loop for optimisation purposes
      //as deleting key from object may require the JavaScript engine to reindex the object's properties.
      for (const key in tempCols) {
        if (key in element) {
          delete element[key];
        }
      }

      element['cols'] = cloneDeep(tempCols);

      return element;
    },
    restructureLabelProperties(element) {
      const isLabelObject = isObject(element.label);
      const labelSource = isLabelObject ? element.label : element;

      //set default values here
      let tempLabel = {
        label: labelSource.label,
        show: true,
        required: false,
        optional: false,
      };

      // Handle 'required' property
      tempLabel.required = isLabelObject
        ? Boolean(labelSource.required)
        : Boolean(labelSource.labelRequired || tempLabel.required);

      // Handle 'optional' property
      tempLabel.optional = isLabelObject
      ? Boolean(labelSource.optional)
      : Boolean(labelSource.labelOptional || tempLabel.optional);

      // Handle 'show' property
      const hasShowProperty = isLabelObject
        ? 'show' in labelSource
        : 'noLabel' in labelSource;

      tempLabel.show = hasShowProperty
        ? (isLabelObject ? Boolean(labelSource.show) : !labelSource.noLabel)
        : tempLabel.show;

      // Final adjustment for 'show' property
      tempLabel.show = tempLabel.show && element.key !== undefined;

      // Clean up original properties
      ['label', 'labelRequired','labelOptional', 'noLabel'].forEach(prop => delete element[prop]);

      element['label'] = cloneDeep(tempLabel);

      return element;
    },
    restructureOnlyLabel(element) {
      //set default value here
      let defaultOnlyLabel = false;

      const hasOnlyLabel = Object.prototype.hasOwnProperty.call(element, 'onlyLabel');
      const hasSectionTitle = Object.prototype.hasOwnProperty.call(element, 'sectionTitle');

      //optimisation, exit function if we already have onlyLabel property
      if (hasOnlyLabel) return element;

      //if onlyLabel variable is provided, then use it, otherwise use default value (false)
      let providedOnlyLabelValue = hasOnlyLabel ? element?.onlyLabel : defaultOnlyLabel;

      //if we have sectionTitle we assume onlyLabel is true
      if (hasSectionTitle) {
        element['onlyLabel'] = true;
      } else {
        element['onlyLabel'] = providedOnlyLabelValue;
      }

      return element;
    },
    //layout size of form/show element
    defaultColsSize(element, breakpoint) {
      //always keep '12' for some kind of elements
      if (!element || element.sectionTitle || element.fullWidth) return "12";

      //DEFAULT: cols size for all the form elements
      switch (breakpoint) {
        case 'cols':
          return '12';
        case 'sm':
          return '6';
        case 'md':
        case 'lg':
        case 'xl':
          return undefined;
        default:
          return '12';
      }
    },
  },
};